import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { deployFetchText, downloadDeployBlob } from '@/api/guardianforge/deploy'
import type { ComparisonRecord } from '@/api/guardianforge/types'
import DecisionModal, { type DecisionModalInitial } from '@/components/deployment/DecisionModal'
import { FrameInspector, type MetricsRow } from '@/components/deployment/FrameInspector'
import {
  DeploymentCard,
  DeploymentError,
  DeploymentJsonView,
  DeploymentSpinner,
  DeploymentStatusBadge,
} from '@/components/deployment/DeploymentUi'
import { useDeployApi } from '@/hooks/useDeployApi'
import { formatKstDateTime } from '@/utils/datetime'
import { useAuth } from '@/auth/useAuth'

interface MetricsState {
  rows: MetricsRow[]
  loading: boolean
  error: string | null
}

interface SummaryModel {
  label: string
  mean_ms: number
  p95_ms: number
  approx_fps: number
  detections_per_frame: number
  total_detections: number
  max_conf?: number
}

interface ComparisonSummary {
  models?: SummaryModel[]
  faster_by_mean_latency?: string | null
  diff?: {
    delta_mean_ms?: number
    delta_detections_per_frame?: number
    delta_total_detections?: number
  } | null
}

export default function ComparisonDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canWriteDecision = hasPermission('model-decision:write')
  const canManageModelRelease = hasPermission('model-release:manage')
  const comparisonId = id ?? ''
  const detail = useDeployApi<ComparisonRecord>(
    comparisonId ? `/comparisons/${comparisonId}` : null,
    undefined,
    5000,
  )
  const [showDecision, setShowDecision] = useState<null | 'approve' | 'reject'>(null)
  const [metrics, setMetrics] = useState<MetricsState>({ rows: [], loading: false, error: null })
  const [frameIdx, setFrameIdx] = useState(0)

  useEffect(() => {
    setFrameIdx(0)
  }, [comparisonId])

  useEffect(() => {
    let cancelled = false
    if (!comparisonId || detail.data?.status !== 'succeeded') {
      setMetrics({ rows: [], loading: false, error: null })
      return
    }
    setMetrics({ rows: [], loading: true, error: null })
    ;(async () => {
      try {
        const text = await deployFetchText(`/comparisons/${comparisonId}/metrics`)
        const rows = parseCsv(text)
        if (!cancelled) setMetrics({ rows, loading: false, error: null })
      } catch (err) {
        if (!cancelled) {
          setMetrics({
            rows: [],
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          })
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [comparisonId, detail.data?.status, detail.data?.metrics_csv_path])

  const summary = detail.data?.summary as ComparisonSummary | null | undefined
  const frameCount = detail.data?.frame_count ?? 0
  const metricsRow = metrics.rows[frameIdx] ?? null

  return (
    <>
      <div className="deployment-toolbar">
        <button className="ghost" onClick={() => navigate('/deployment/comparisons')}>
          목록
        </button>
        <h2 style={{ margin: 0 }}>
          비교 상세{' '}
          <span className="deployment-muted deployment-mono" style={{ fontWeight: 400, fontSize: '0.9em' }}>
            {comparisonId.slice(0, 12)}...
          </span>
        </h2>
        {detail.loading && !detail.data && <DeploymentSpinner />}
      </div>

      <DeploymentError error={detail.error} />

      {detail.data && (
        <>
          <DeploymentCard title="요약">
            <div className="deployment-grid cols-3">
              <div>
                <label>Status</label>
                <DeploymentStatusBadge status={detail.data.status} />
              </div>
              <div>
                <label>Frames</label>
                <div className="deployment-mono">{detail.data.frame_count ?? '-'}</div>
              </div>
              <div>
                <label>Comparator job</label>
                <div className="deployment-mono">{detail.data.comparator_job_id ?? '-'}</div>
              </div>
              <div>
                <label>Candidate</label>
                <div className="deployment-mono">{detail.data.candidate_ref}</div>
              </div>
              <div>
                <label>Baseline</label>
                <div className="deployment-mono">
                  {detail.data.baseline_model_key
                    ? `${detail.data.baseline_model_key}/${detail.data.baseline_version}`
                    : '(none)'}
                </div>
              </div>
              <div>
                <label>Test video</label>
                <div className="deployment-mono deployment-muted">{detail.data.test_video_ref}</div>
              </div>
              <div>
                <label>Created</label>
                <div className="deployment-mono deployment-muted">{formatKstDateTime(detail.data.created_at)}</div>
              </div>
              <div>
                <label>Started</label>
                <div className="deployment-mono deployment-muted">{formatKstDateTime(detail.data.started_at)}</div>
              </div>
              <div>
                <label>Finished</label>
                <div className="deployment-mono deployment-muted">{formatKstDateTime(detail.data.finished_at)}</div>
              </div>
              <div>
                <label>faster_by_mean_latency</label>
                <div className="deployment-mono">{summary?.faster_by_mean_latency ?? '-'}</div>
              </div>
            </div>

            {detail.data.error && <div className="deployment-error-box" style={{ marginTop: 12 }}>{detail.data.error}</div>}
          </DeploymentCard>

          {summary?.models && (
            <DeploymentCard title="모델별 메트릭">
              <table className="deployment-table">
                <thead>
                  <tr>
                    <th>label</th>
                    <th>mean ms</th>
                    <th>p95 ms</th>
                    <th>fps</th>
                    <th>det/frame</th>
                    <th>total det</th>
                    <th>max conf</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.models.map((model) => (
                    <tr key={model.label}>
                      <td>{model.label}</td>
                      <td className="deployment-mono">{model.mean_ms?.toFixed(2)}</td>
                      <td className="deployment-mono">{model.p95_ms?.toFixed(2)}</td>
                      <td className="deployment-mono">{model.approx_fps?.toFixed(2)}</td>
                      <td className="deployment-mono">{model.detections_per_frame?.toFixed(2)}</td>
                      <td className="deployment-mono">{model.total_detections}</td>
                      <td className="deployment-mono">{model.max_conf?.toFixed(2) ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {summary.diff && (
                <div className="deployment-muted" style={{ marginTop: 6 }}>
                  Δ mean ms: <span className="deployment-mono">{summary.diff.delta_mean_ms?.toFixed(2)}</span>
                  {' / '}Δ det/frame:{' '}
                  <span className="deployment-mono">{summary.diff.delta_detections_per_frame?.toFixed(2)}</span>
                  {' / '}Δ total det: <span className="deployment-mono">{summary.diff.delta_total_detections}</span>
                </div>
              )}
            </DeploymentCard>
          )}

          {detail.data.status === 'succeeded' && (
            <DeploymentCard title="Frame Inspector">
              <FrameInspector
                comparisonId={comparisonId}
                frameCount={frameCount}
                idx={frameIdx}
                onIdxChange={setFrameIdx}
                metricsRow={metricsRow}
              />
              {metrics.loading && <div className="deployment-muted">metrics CSV 로딩 중...</div>}
              {metrics.error && <div className="deployment-error-box">metrics CSV: {metrics.error}</div>}
              <div className="deployment-flex" style={{ marginTop: 6 }}>
                <button
                  onClick={() => downloadDeployBlob(`/comparisons/${comparisonId}/metrics`, `${comparisonId}-metrics.csv`)}
                >
                  metrics CSV 다운로드
                </button>
              </div>
            </DeploymentCard>
          )}

          {canWriteDecision && (
            <div className="deployment-flex" style={{ marginTop: 18 }}>
              {canManageModelRelease && (
                <button
                  className="primary"
                  disabled={detail.data.status !== 'succeeded'}
                  onClick={() => setShowDecision('approve')}
                >
                  Approve & Register
                </button>
              )}
              <button className="danger" onClick={() => setShowDecision('reject')}>
                Reject
              </button>
            </div>
          )}

          <details style={{ marginTop: 18 }}>
            <summary className="deployment-muted">Raw record</summary>
            <DeploymentJsonView data={detail.data} />
          </details>

          {showDecision && canWriteDecision && (showDecision !== 'approve' || canManageModelRelease) && (
            <DecisionModal
              comparisonId={detail.data.id}
              candidateSource={detail.data.candidate_source}
              candidateRef={detail.data.candidate_ref}
              initial={decisionInitial(detail.data)}
              kind={showDecision}
              onClose={() => setShowDecision(null)}
              onDone={() => {
                setShowDecision(null)
                detail.refresh()
              }}
            />
          )}
        </>
      )}
    </>
  )
}

function decisionInitial(comparison: ComparisonRecord): DecisionModalInitial {
  const extra = comparison.candidate_extra
  const modelKey = String(extra?.model_name ?? comparison.candidate_ref.split('/')[0] ?? '')
  return {
    modelKey,
    version: String(extra?.model_version ?? comparison.candidate_ref.split('/').slice(-1)[0] ?? ''),
    format: detectFormat(modelKey),
    mlflowRunId: optionalString(extra?.run_id),
    mlflowModelName: optionalString(extra?.model_name),
    mlflowModelVersion: optionalString(extra?.model_version),
  }
}

function optionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  const text = String(value)
  return text || undefined
}

function detectFormat(modelKey: string): string {
  if (modelKey.endsWith('_openvino_int8')) return 'openvino_int8'
  if (modelKey.endsWith('_openvino_fp16')) return 'openvino_fp16'
  if (modelKey.endsWith('_openvino_fp32')) return 'openvino_fp32'
  return 'pt'
}

function parseCsv(text: string): MetricsRow[] {
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) return []
  const headers = lines[0].split(',')
  return lines.slice(1).map((line) => {
    const cells = line.split(',')
    const row: MetricsRow = {}
    headers.forEach((header, index) => {
      row[header] = cells[index]
    })
    return row
  })
}
